import type { HubCard, NewsItem, ServiceGroup } from '../core/types'

/**
 * Content for 필라델피아 기쁨의 교회 (Jubilee Presbyterian Church).
 *
 * Everything here is transcribed from jubilee-km.org. Once /api/content is
 * wired to KV this file becomes the offline fallback, not the source of truth
 * — so keep it valid rather than letting it rot.
 */

export const CHURCH = {
  nameKo: '필라델피아 기쁨의 교회',
  nameEn: 'Jubilee Presbyterian Church',
  address: '1911 W. Marshall St., West Norriton, PA 19403',
  tel: '(610) 630-6300',
  site: 'jubilee-km.org',
} as const

/** The English Ministry, as jubileechurch.net presents it. */
export const EM = {
  name: 'Jubilee Presbyterian Church',
  subtitle: 'Reformed Church in Philadelphia',
  tagline: 'Proclaiming Grace, Celebrating Freedom',
  taglineRef: 'Lev. 25:10',
  site: 'jubileechurch.net',
  schedule: [
    { name: 'Sunday Worship', time: '9:30 a.m.' },
    { name: 'IGM Chapels', time: '9:30 a.m.' },
    { name: 'Post-Worship Study Groups', time: '11:30 a.m.' },
    { name: 'IGM Catechism Classes', time: '11:30 a.m.' },
  ],
} as const

/**
 * The three panels. Fully's Start URL carries the panel id:
 *   https://<host>/?panel=lobby
 * so each screen can lead with what matters where it hangs.
 */
export const PANELS = {
  lobby: '본당 로비',
  fellowship: '친교실',
  education: '교육관',
} as const

export type PanelId = keyof typeof PANELS

export const HUB_CARDS: HubCard[] = [
  {
    id: 'bulletin',
    title: '이번주 주보',
    subtitle: '예배 순서와 공동체 소식',
    target: 'bulletin',
    icon: 'M6 2h9l5 5v15H6zM15 2v5h5M9 12h8M9 16h8',
  },
  {
    id: 'worship',
    title: '예배 안내',
    subtitle: '주일 예배와 주중 집회',
    target: 'worship',
    icon: 'M12 3v18M7 8h10M5 21h14',
  },
  {
    id: 'news',
    title: '교회 소식',
    subtitle: '공지와 행사 안내',
    target: 'news',
    icon: 'M4 5h16v14H4zM8 9h8M8 13h8M8 17h5',
  },
  {
    id: 'directions',
    title: '오시는 길',
    subtitle: '주소와 연락처',
    target: 'directions',
    icon: 'M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11zM12 10h.01',
  },
]

export const SERVICES: ServiceGroup[] = [
  {
    heading: '주일 예배',
    entries: [
      { name: '주일 1부 예배', time: '8:00 AM' },
      { name: '주일 2부 예배', time: '9:30 AM' },
      { name: 'EM 예배', note: '영어 예배', time: '9:30 AM' },
      { name: '주일 3부 예배', time: '11:30 AM' },
    ],
  },
  {
    heading: '교회학교',
    entries: [
      { name: '영아부', note: '4개월~30개월', time: '9:30 AM · 11:30 AM' },
      { name: '유아부', note: '30개월~Pre-K', time: '9:30 AM · 11:30 AM' },
      { name: '유년부', note: 'Kinder~2학년', time: '9:30 AM' },
      { name: '초등부', note: '3~5학년', time: '9:30 AM' },
      { name: '청소년부', note: '6~12학년', time: '9:30 AM' },
    ],
  },
  {
    heading: '주중 집회',
    entries: [
      { name: '새벽 기도회', time: '화-금 6:00 AM' },
      { name: '금요 집회', time: '금 7:30 PM' },
      { name: 'AWANA', note: '어린이 금요 프로그램', time: '금 7:30 PM' },
      { name: '청소년 금요 집회', time: '금 7:30 PM' },
    ],
  },
]

export const NEWS: NewsItem[] = [
  { title: "2026 열린말씀 컨퍼런스 '하나님의 형상대로'", date: '2026.04.03' },
  { title: '2025 성탄축하연주회', date: '2025.12.14' },
  { title: '2025년 하반기 성경읽기 모임 안내', date: '2025.08.29' },
  { title: '교회설립 27주년 감사예배', date: '2025.06.14' },
]

/**
 * The weekly bulletin is published as ONE landscape JPEG (2100x1275) with no
 * text layer — see screens/bulletin.ts for why that is a problem on a portrait
 * panel and what the viewer does about it.
 */
export const BULLETIN = {
  label: '2026년 8월 30일 주보',
  // TODO: replace with /api/bulletin once KV holds the current week, and mirror
  // the image into R2 so a network blip cannot blank the panel.
  image:
    'https://media.jubilee-km.org/images/bulletin_board/20260829113541_f970f15cd2094fecaae850e1fba58681_8-30-2026_page_1.jpg',
  width: 2100,
  height: 1275,
} as const
